import { createContext, useCallback, useEffect, useState } from "react";

import { showAPIErrorMessage } from "utilities/apiHandler";

import { type VendorProductInventoryResponse } from "entity/vendor/common/vendor.types";
import {
    type IVendorProductsHandler,
    VendorProductsHandler,
} from "entity/vendor/common/vendor.utility.api.handler";

export interface IVendorProductInfoContextProps {
    /**
     * Called when the user wants to fetch vendor product information. Will make an API call and
     * eventually populate vendorProductInventory. Displays toast message on error, but will also
     * rethrow exception.
     */
    onVendorProductInfoRequest: () => Promise<void>;
    /**
     * API response for vendor product inventory.
     */
    vendorProductInventory?: VendorProductInventoryResponse;
}

export const VendorProductInfoContext = createContext<IVendorProductInfoContextProps>({
    onVendorProductInfoRequest: async () => {
        throw new Error(
            "Vendor product info request was made without context provider. Make sure all vendor product info consumers are inside of the VendorProductInfoContainer",
        );
    },
});

export interface IVendorProductInfoContainerProps {
    /**
     * List of vendor product ids to query for when a request is made. Any changes to this array
     * will clear the current vendor inventory response stored int state.
     */
    vendorProductIds: number[];
    /**
     * Used for mocking/testing
     */
    handler?: IVendorProductsHandler;

    children: React.ReactNode;
}

const defaultVendorProductsHandler = new VendorProductsHandler();

/**
 * Container for managing vendor product inventory requests. Handles API calls, state management,
 * and provides the VendorProductInfoContext. The root purpose of this component is to pool vendor
 * product inventory requests into a single API call due to performance concerns from our vendor
 * partners.
 */
export const VendorProductInfoContainer: React.FC<IVendorProductInfoContainerProps> = ({
    vendorProductIds,
    handler = defaultVendorProductsHandler,
    children,
}) => {
    const [vendorProductInventory, setVendorProductInventory] = useState<
        VendorProductInventoryResponse | undefined
    >(undefined);

    const handleVendorProductInfoRequest = useCallback(async () => {
        if (!vendorProductInventory) {
            try {
                const newVendorProdInvResp =
                    await handler.getInventoryInformation(vendorProductIds);
                setVendorProductInventory(newVendorProdInvResp);
            } catch (e) {
                showAPIErrorMessage(e);
                throw e;
            }
        }
    }, [handler, vendorProductIds, vendorProductInventory]);

    useEffect(() => {
        // Clear out the API response if the vendor productIds array changes
        setVendorProductInventory(undefined);
    }, [vendorProductIds]);

    return (
        <VendorProductInfoContext.Provider
            value={{
                onVendorProductInfoRequest: handleVendorProductInfoRequest,
                vendorProductInventory,
            }}
        >
            {children}
        </VendorProductInfoContext.Provider>
    );
};
